import React, { useState, useEffect, useMemo } from "react"; // Added useMemo
import { useActsAsEmployee } from "@/hooks/useActsAsEmployee";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Calendar,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  X,
  Zap,
  CalendarOff,
  ChevronRight,
  Wallet,
  Info,
  Banknote,
  CalendarIcon,
  Paperclip,
  User,
  MessageSquare,
  ArrowRight,
  MoreHorizontal,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import { format, set } from "date-fns";
import {
  cancelLeaveRequest,
  getEmployeeLeaveDashboard,
  getEmployeeLeaveRequests,
  submitLeaveRequest,
  viewDocument,
} from "@/services/leaveService";
import ApplyLeaveDialog from "@/components/common/LeaveRequestDialog";
import { fetchHolidays } from "@/services/adminService";
import {
  fetchEncashmentDetails,
  requestLeaveEncashment,
} from "@/services/employeeService";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import MultipleSelector from "@/components/common/MultiSelect";
import ConfirmDialog from "@/components/ConfirmDialog";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

const EmployeeLeave = (employeeId) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5; // Set a default number of items per page

  const [initialLoading, setInitialLoading] = useState(true);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [employeeData, setEmployeeData] = useState(null);
  const [leaveEntitlement, setLeaveEntitlement] = useState(null);
  const [dashboardLeaveEntitlement, setDashboardLeaveEntitlement] = useState(
    []
  );
  const [dashboardLeaveTypes, setDashboardLeaveTypes] = useState([]);
  const [isOnProbation, setIsOnProbation] = useState(false);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [applying, setApplying] = useState(false);
  const [workingDaysConfig, setWorkingDaysConfig] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancellingLeave, setCancellingLeave] = useState(null);
  const [cancellationReason, setCancellationReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [companyHolidays, setCompanyHolidays] = useState([]);
  const [encashMentData, setEncashMentData] = useState({
    total_available: 0,
    encashable: 0,
    fixedAmountPerDay: 0,
    history: [],
    leaveBalances: [],
  });

  const [selectedLeaves, setSelectedLeaves] = useState([]);
  const [leaveEncashDays, setLeaveEncashDays] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
 
  // The employee's own leave page (also an admin's, in My workspace) — not the
  // admin's view of someone else's leave on their profile.
  const actsAsEmployee = useActsAsEmployee();

  useEffect(() => {
    let isMounted = true;

    const initLeavePage = async () => {
      if (isMounted) setInitialLoading(true);
      try {
        const tasks = [handleFetchHolidays(), loadDashboard(), fetchLeaveRequests()];
        if (actsAsEmployee) {
          tasks.push(getEncashmentData());
        }
        await Promise.all(tasks);
      } finally {
        if (isMounted) setInitialLoading(false);
      }
    };

    initLeavePage();

    return () => {
      isMounted = false;
    };
  }, [actsAsEmployee]);

  const handleFetchHolidays = async () => {
    try {
      const res = await fetchHolidays();

      if (res && Array.isArray(res)) {
        const currentYear = new Date().getFullYear();

        const filteredAndSorted = res
          .filter((h) => new Date(h.date).getFullYear() === currentYear)
          .sort((a, b) => {
            const da = new Date(a.date);
            const db = new Date(b.date);
            return da.getMonth() - db.getMonth() || da.getDate() - db.getDate();
          });

        setCompanyHolidays(filteredAndSorted);
      }
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error(getErrorMessage(error, "Failed to fetch holidays"));
      }
    }
  };

  const loadDashboard = async () => {
    try {
      const res = await getEmployeeLeaveDashboard(employeeId);
      setEmployeeData(res.employee);
      setIsOnProbation(res.employee.is_on_probation);
      setLeaveEntitlement(res.leave_balance || []);
      setWorkingDaysConfig(res.working_days_config);
      setHolidays(res.holidays);
      setDashboardLeaveEntitlement(res.dashboard_leave_balance || []);
      setDashboardLeaveTypes(res.dashboard_leave_types || []);
    } catch (err) {
      toast.error("Failed to load dashboard");
    }
  };

  const fetchLeaveRequests = async () => {
    try {
      const response = await getEmployeeLeaveRequests(employeeId);
      setLeaveRequests(response);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching leave requests:", error);
      toast.error("Failed to load leave requests");
    }
  };

  // --- Pagination Logic ---
  const totalPages = Math.ceil(leaveRequests.length / itemsPerPage);

  const visiblePages = useMemo(() => {
    if (totalPages <= 4) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 2) {
      return [1, 2, "ellipsis-end", totalPages];
    }

    if (currentPage >= totalPages - 1) {
      return [1, "ellipsis-start", totalPages - 1, totalPages];
    }

    return [1, "ellipsis-start", currentPage, "ellipsis-end", totalPages];
  }, [currentPage, totalPages]);

  const paginatedRequests = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return leaveRequests.slice(startIndex, endIndex);
  }, [leaveRequests, currentPage, itemsPerPage]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSubmitLeave = async (formData, calculatedDays, documentFile) => {
    setApplying(true);
    try {
      await submitLeaveRequest(
        {
          leave_id: formData.leave_id,
          leave_name: formData.leave_name,
          start_date: formData.start_date,
          end_date: formData.end_date,
          reason: formData.reason,
          half_day: formData.half_day,
          half_day_start_time: formData.half_day
            ? formData.half_day_start_time
            : "",
          half_day_end_time: formData.half_day
            ? formData.half_day_end_time
            : "",
          days: calculatedDays,
        },
        documentFile,
        employeeId
      );

      toast.success("Leave request submitted successfully!");
      setShowApplyDialog(false);
      fetchLeaveRequests();
      loadDashboard();
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.detail ||
        "Failed to submit leave";
      toast.error(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setApplying(false);
    }
  };

  const getStatusBadge = (status) => {
     const display = String(status || "").toLowerCase();

     const base =
       "capitalize rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide border";

    const styles = {
      pending: "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200",
      approved: "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-200",
      rejected: "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200",
      cancelled: "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200",
    };
    return (
      <Badge variant="outline" className={`${base} ${styles[display] || "border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-900/20 dark:text-gray-200"}`}>
        {display}
      </Badge>
    );
  };

  /**
   * Calendar day of a stored leave date, as a comparable UTC-midnight stamp.
   *
   * Leave dates are written as UTC midnight standing for a calendar date (see
   * createLeaveRequest on the backend), so the day must be read back off the
   * UTC components. Reading them locally shifts the date by one in any negative
   * UTC offset, which is the timezone bug this avoids.
   */
  const leaveDateKey = (value) => {
    const date = new Date(value);
    return Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate()
    );
  };

  /** Today as the same kind of stamp, taken from the viewer's LOCAL calendar. */
  const todayDateKey = () => {
    const now = new Date();
    return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  };

  const canCancelLeave = (request) => {
    if (request.status !== "pending" && request.status !== "approved") {
      return false;
    }

    // Strictly FUTURE start dates only. A leave starting today is already in
    // effect — attendance for the day is being decided now — so it is no longer
    // the employee's to withdraw; the same rule is enforced on the cancel
    // endpoint. Multi-day leave is judged on its start date.
    return leaveDateKey(request.start_date) > todayDateKey();
  };

  const openCancelDialog = (request) => {
    setCancellingLeave(request);
    setCancellationReason("");
    setShowCancelDialog(true);
  };

  const handleCancelLeave = async () => {
    if (!cancellationReason.trim()) {
      toast.error("Please provide a reason for cancellation");
      return;
    }

    setCancelling(true);
    try {
      await cancelLeaveRequest(
        cancellingLeave.id,
        cancellationReason,
        employeeId
      );

      toast.success("Leave request cancelled successfully");

      setShowCancelDialog(false);
      setCancellingLeave(null);
      setCancellationReason("");
      fetchLeaveRequests();
      loadDashboard();
    } catch (error) {
      console.error("Error cancelling leave:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.detail ||
          "Failed to cancel leave request"
      );
    } finally {
      setCancelling(false);
    }
  };

  const viewLeaveDocument = async (key) => {
    try {
      const response = await viewDocument(key);
      const { url } = response;
      window.open(url, "_blank");
    } catch {
      toast.error("Failed to load document");
    }
  };

  const getEncashmentData = async () => {
    try {
      const response = await fetchEncashmentDetails();
      console.log("getEncashmentData", response);
      setEncashMentData(response);
    } catch (error) {
      toast.error(error.message || "Failed to load encashment data");
    }
  };

  const applyLeaveEncashment = async () => {
    const values = leaveEncashDays
      ? Object.values(leaveEncashDays).map((v) => Number(v ?? 0))
      : [];

    const hasAnyZeroOrLess =
      values.length > 0 ? values.some((v) => v <= 0) : true;

    if (hasAnyZeroOrLess) {
      toast.error("No. of encashment requested days cannot be 0.");
      return;
    }
    
    const payload = {
      days_requested: selectedLeaves.map((leaveId) => ({
        leave_id: leaveId,
        days: leaveEncashDays[leaveId] || 0,
      })),
      amount_per_day: encashMentData.fixedAmountPerDay,
    };

    try {
      await requestLeaveEncashment(payload);
      toast.success("Leave encashment request submitted");
      setSelectedLeaves([]);
      setLeaveEncashDays({});
      getEncashmentData();
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to submit encashment request");
    } finally {
      setConfirmOpen(false);
    }
  };

  const handleOpenDetails = (request) => {
    setSelectedRequest(request);
    setIsDialogOpen(true);
  };

  const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onloadend = () => {
      const base64 = String(reader.result).split(",")[1] || "";
      resolve(base64);
    };
    reader.readAsDataURL(blob);
  });

  const getExtensionFromMime = (mime) => {
    if (!mime) return "bin";
    if (mime.includes("pdf")) return "pdf";
    if (mime.includes("png")) return "png";
    if (mime.includes("jpeg") || mime.includes("jpg")) return "jpg";
    if (mime.includes("msword")) return "doc";
    if (mime.includes("officedocument")) return "docx";
    return "bin";
  };

  const downloadLeaveDocument = async (key) => {
    try {
      const response = await viewDocument(key);
      const { url: signedUrl } = response;

      // ======================
      // MOBILE (Capacitor)
      // ======================
      if (Capacitor.isNativePlatform()) {
        const fileResponse = await fetch(signedUrl);
        if (!fileResponse.ok) throw new Error("Download failed");

        const blob = await fileResponse.blob();

        const mimeType = blob.type;
        const extension = getExtensionFromMime(mimeType);

        const fileName = `leave-document.${extension}`;

        const base64 = await blobToBase64(blob);

        const writeResult = await Filesystem.writeFile({
          path: fileName,
          data: base64,
          directory: Directory.Documents,
          recursive: true,
        });

        await Share.share({
          title: "Leave Document",
          text: fileName,
          url: writeResult.uri,
          dialogTitle: "Open / Save document",
        });

        toast.success("Document downloaded");
        return;
      }

      // ======================
      // WEB
      // ======================
      const fileResponse = await fetch(signedUrl);
      if (!fileResponse.ok) throw new Error("Download failed");

      const blob = await fileResponse.blob();
      const extension = getExtensionFromMime(blob.type);
      const fileName = `leave-document.${extension}`;

      const objectUrl = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(objectUrl);

      toast.success("Document downloaded");
    } catch (error) {
      console.error(error);
      toast.error("Failed to download document");
    }
  };

  const leaveOptions = encashMentData.leaveBalances.map((leave) => ({
    label: leave.leave_name,
    value: leave.leave_id || leave._id,
  }));

  const totalEncashDays = Object.values(leaveEncashDays).reduce(
    (sum, days) => sum + Number(days || 0),
    0,
  );

  const estimatedPayoff = Math.round(
    totalEncashDays * Number(encashMentData?.fixedAmountPerDay || 0),
  );

  if (initialLoading) {
    return <EmployeeLeaveSkeleton employeeId={employeeId} />;
  }

  return (
    <div className="space-y-6">
      {/* 1. Header and Apply Button (Untouched) */}
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center border-b pb-4 dark:border-gray-700">
        <div>
          {!employeeId.employeeId && (
            <>
              <h1 className="text-2xl font-extrabold text-gray-900 dark:text-gray-100">
                Leave Management
              </h1>
              <p className="text-gray-500 mt-1">
                Easily apply for and track your personal and annual leave
                requests.
              </p>
            </>
          )}
        </div>
        <div className="flex flex-wrap justify-start md:justify-end mt-1 gap-4 gap-y-2">
          {actsAsEmployee && (
            <div className="flex justify-end">
              <Dialog>
                <DialogTrigger asChild>
                  <Button>View Holidays</Button>
                </DialogTrigger>
                <DialogContent className="">
                  <DialogHeader>
                    <DialogTitle>Company Holidays</DialogTitle>
                  </DialogHeader>

                  <div className="mt-4 max-h-80 overflow-y-auto pr-1">
                    {companyHolidays.length > 0 ? (
                      <div className="space-y-3">
                        {companyHolidays.map((holiday, index) => (
                          <div
                            key={index}
                            className="flex justify-between items-center text-sm rounded-md px-2 py-2 hover:bg-muted/40 transition"
                          >
                            <span className="font-medium text-primary">
                              {holiday.name}
                            </span>

                            <span className="text-muted-foreground">
                              {new Date(holiday.date).toDateString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-10 text-center">
                        <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-4">
                          <CalendarIcon className="h-5 w-5 text-muted-foreground" />
                        </div>

                        <p className="text-sm font-medium">
                          No holidays announced
                        </p>

                        <p className="text-xs text-muted-foreground mt-1">
                          Your company has not added any holidays yet.
                        </p>
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}
          {actsAsEmployee && (
            <Button
              className="flex items-center space-x-2 shadow-md"
              onClick={() => setShowApplyDialog(true)}
            >
              <Plus className="h-4 w-4" />
              <span>New Leave Request</span>
            </Button>
          )}
        </div>
        <ApplyLeaveDialog
          isOpen={showApplyDialog}
          setIsOpen={setShowApplyDialog}
          leaveEntitlement={leaveEntitlement}
          leaveTypes={[]}
          workingDaysConfig={workingDaysConfig}
          holidays={holidays}
          isEmployeeView={true}
          isEmployeeOnProbation={isOnProbation}
          onSubmit={handleSubmitLeave}
        />
      </header>
      {/* 2. Probation Warning Card (Untouched) */}
      {isOnProbation && (
        <Card className="border-l-4 border-orange-500 bg-orange-50 dark:bg-orange-950/50 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-start space-x-4">
              <AlertCircle className="h-7 w-7 text-orange-600 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold text-orange-800 dark:text-orange-300">
                  Probation Active
                </h3>
                {employeeData?.probation_end_date && (
                  <p className="text-sm text-orange-600 dark:text-orange-500 mt-2">
                    Your probation period is scheduled to end on:{" "}
                    <strong>
                      {format(
                        new Date(employeeData.probation_end_date),
                        "dd MMMM yyyy",
                      )}
                    </strong>
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {/* 3. Dynamic Leave Balance Cards (Untouched) */}
      <div className="space-y-6">
        <section
          className="grid gap-6"
          // Using inline style for the custom responsive grid setup, as defined previously
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          }}
        >
          {dashboardLeaveEntitlement?.map((lb) => {
            const type = dashboardLeaveTypes.find((t) => t._id === lb.leave_id);
            const available =
              isOnProbation && !type.is_allow_probation
                ? 0
                : lb.total_allocated - (lb.used || 0);
            const percentage =
              lb.total_allocated > 0
                ? (available / lb.total_allocated) * 100
                : 0;
            const isLow = available < 3 && available > 0;
            const cardClass = isLow
              ? "border-yellow-400 dark:border-yellow-600 border-2"
              : "border-gray-100 dark:border-gray-700 border";

            return (
              <Card
                key={lb.leave_id}
                className={`shadow-lg hover:shadow-xl transition-all duration-300 ${cardClass} ${
                  isOnProbation && !type.is_allow_probation
                    ? "opacity-60 grayscale cursor-not-allowed"
                    : ""
                }`}
              >
                <CardContent className="p-5">
                  {/* FIX START: Apply flex and min-w-0 to the text container */}
                  <div className="flex items-start justify-between space-x-3">
                    <div className="flex-grow min-w-0">
                      {/* Leave Type Name: Ensure truncation works within the space */}
                      <p
                        className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest truncate"
                        title={type?.leave_name || lb.leave_name}
                      >
                        {type?.leave_name || lb.leave_name}
                      </p>

                      {/* Available Days: Bolder and higher contrast */}
                      <p className="text-3xl font-extrabold text-blue-700 dark:text-blue-400 mt-1">
                        {available}
                        <span className="text-base font-medium text-gray-500 ml-1">
                          days
                        </span>
                      </p>

                      {/* Usage Breakdown: Clear secondary info */}
                      <p className="text-xs text-gray-400 mt-1">
                        {isOnProbation && !type.is_allow_probation
                          ? "Not available (Probation)"
                          : `${lb.used || 0} used / ${
                              lb.total_allocated
                            } total`}
                      </p>
                    </div>
                    {/* FIX END: Text container stops growing here */}

                    {/* Icon Circle: This element is now safe from overflow */}
                    <div
                      className={`w-10 h-10 ${
                        isLow
                          ? "bg-yellow-100 dark:bg-yellow-900/50"
                          : "bg-blue-100 dark:bg-blue-900/50"
                      } rounded-full flex items-center justify-center flex-shrink-0`}
                    >
                      <Calendar
                        className={`h-5 w-5 ${
                          isLow
                            ? "text-yellow-600 dark:text-yellow-400"
                            : "text-blue-600 dark:text-blue-400"
                        }`}
                      />
                    </div>
                  </div>

                  {/* Progress Bar (Visually Separated) */}
                  <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                    <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className={`${
                          isLow ? "bg-yellow-500" : "bg-blue-600"
                        } h-2 rounded-full transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>

                  {isLow && (
                    <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-2 font-semibold flex items-center space-x-1">
                      <AlertCircle className="h-3 w-3" />
                      <span>Low balance warning</span>
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Total Available Card (No changes needed here as the internal structure is already safe) */}
          <Card
            className={`shadow-lg transition-shadow bg-purple-50 dark:bg-gray-900/50 border-2 border-purple-300 dark:border-purple-800`}
          >
            <CardContent className="p-5 h-full flex flex-col justify-between">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-bold text-purple-700 dark:text-purple-300 uppercase tracking-widest">
                    Total Leaves
                  </p>
                  <div className="flex items-baseline space-x-1 mt-1">
                    <p className="text-3xl font-extrabold text-purple-700 dark:text-purple-400">
                      {dashboardLeaveEntitlement?.reduce((acc, lb) => {
                        const type = dashboardLeaveTypes.find(
                          (t) => t._id === lb.leave_id,
                        );

                        // If on probation, only include allowed leaves
                        if (isOnProbation && !type?.is_allow_probation) {
                          return acc;
                        }

                        return acc + (lb.total_allocated - (lb.used || 0));
                      }, 0) || 0}
                    </p>
                    <span className="text-base text-gray-500">days</span>
                  </div>
                </div>

                <div className="w-10 h-10 bg-purple-200 dark:bg-purple-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                  <Zap className="h-5 w-5 text-purple-700 dark:text-purple-400" />
                </div>
              </div>

              <p className="text-xs text-gray-600 dark:text-gray-400 mt-4">
                Combined balance across all leave categories.
              </p>
            </CardContent>
          </Card>
        </section>
      </div>
      {/* 4. Leave Requests History (Spacious Table + Pagination) */}
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="text-xl font-bold flex items-center space-x-2">
            <CalendarOff className="h-6 w-6 text-gray-600" />
            <span>Leave Request History</span>
          </CardTitle>
          <CardDescription>
            Showing {paginatedRequests.length} of {leaveRequests.length}{" "}
            requests.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {leaveRequests.length === 0 ? (
            <div className="text-center py-10 bg-gray-100/60 dark:bg-gray-800/50">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300">
                No Leave Requests Found
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                Leave requests you submit will appear here.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50/50 dark:bg-gray-900/50 border-b">
                    <TableHead className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-gray-500">
                      Type
                    </TableHead>
                    <TableHead className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-gray-500">
                      Date Range
                    </TableHead>
                    <TableHead className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-gray-500 text-center">
                      Days
                    </TableHead>
                    <TableHead className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-gray-500">
                      Status
                    </TableHead>
                    <TableHead className="py-4 px-6 text-xs font-bold uppercase tracking-wider text-gray-500 text-right">
                      Action
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedRequests.map((request) => (
                    <TableRow
                      key={request?.id || Math.random()}
                      onClick={() => handleOpenDetails(request)}
                      className="group cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors border-b border-gray-100 dark:border-gray-800 last:border-0"
                    >
                      <TableCell className="py-4 px-6 font-semibold text-gray-900 dark:text-gray-100">
                        {request?.leave_name || "N/A"}
                      </TableCell>
                      <TableCell className="py-4 px-6 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center space-x-2">
                          <span>
                            {request?.start_date
                              ? format(
                                  new Date(request.start_date),
                                  "dd MMM yyyy",
                                )
                              : "N/A"}
                          </span>
                          {request?.start_date &&
                            request?.end_date &&
                            request.start_date !== request.end_date && (
                              <>
                                <span className="text-gray-400">→</span>
                                <span>
                                  {format(
                                    new Date(request.end_date),
                                    "dd MMM yyyy",
                                  )}
                                </span>
                              </>
                            )}
                          {request?.half_day && (
                            <Badge
                              variant="outline"
                              className="text-[10px] uppercase font-bold px-1.5 py-0 border-blue-200 bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
                            >
                              Half
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-4 px-6 text-center font-mono font-bold text-gray-700 dark:text-gray-300">
                        {request?.days !== undefined
                          ? String(request.days).padStart(2, "0")
                          : "00"}
                      </TableCell>
                      <TableCell className="py-4 px-6">
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(request?.status)}
                          {request?.status === "pending" && request?.approval_stage && (
                            <div className="mt-1 text-[11px] text-muted-foreground">
                              {request.approval_stage === "manager"
                                ? "Awaiting manager approval"
                                : "Awaiting admin approval"}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell
                        className="py-4 px-6 text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-400 hover:text-[#3194a0] dark:hover:text-[#3194a0] hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="w-56 p-1.5 shadow-2xl border-slate-200 dark:border-slate-800"
                          >
                            <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-400 font-bold px-2 py-1.5">
                              Request Actions
                            </DropdownMenuLabel>

                            <DropdownMenuSeparator />

                            {/* View Details */}
                            <DropdownMenuItem
                              className="
      text-[#3194a0] dark:text-[#4ec0c9]
      cursor-pointer text-xs font-bold
      focus:bg-[#3194a0]/10
      dark:focus:bg-[#3194a0]/20
      rounded-md py-2.5 px-2.5
      group/item flex items-center justify-between transition-colors
    "
                              onClick={() => handleOpenDetails(request)}
                            >
                              <div className="flex items-center">
                                <div
                                  className="
        w-7 h-7 rounded-full
        bg-[#3194a0]/10 dark:bg-[#3194a0]/20
        flex items-center justify-center mr-3
        group-focus/item:bg-[#3194a0]/20 transition-colors
      "
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </div>

                                <span>View Details</span>
                              </div>

                              <ChevronRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all" />
                            </DropdownMenuItem>

                            {/* Cancel Leave */}
                            {canCancelLeave(request) && (
                              <DropdownMenuItem
                                className="
        text-rose-600 dark:text-rose-400
        cursor-pointer text-xs font-bold
        focus:bg-rose-50
        dark:focus:bg-rose-900/20
        rounded-md py-2.5 px-2.5
        group/item flex items-center justify-between transition-colors
      "
                                onClick={() => openCancelDialog(request)}
                              >
                                <div className="flex items-center">
                                  <div
                                    className="
          w-7 h-7 rounded-full
          bg-rose-50 dark:bg-rose-900/20
          flex items-center justify-center mr-3
          group-focus/item:bg-rose-100
          dark:group-focus/item:bg-rose-900/30
          transition-colors
        "
                                  >
                                    <XCircle className="w-3.5 h-3.5" />
                                  </div>

                                  <span>Cancel Leave</span>
                                </div>

                                <ChevronRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all" />
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>

        {/* --- Pagination Controls --- */}
        {leaveRequests.length > itemsPerPage && (
          <div className="flex flex-col gap-3 p-4 border-t dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
            <p className="shrink-0 text-sm text-gray-500 dark:text-gray-400">
              Page {currentPage} of {totalPages}
            </p>
            <Pagination className="mx-0 w-auto justify-center sm:justify-end">
              <PaginationContent className="flex-wrap justify-center">
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-disabled={currentPage === 1}
                    className={
                      currentPage === 1
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>

                {visiblePages.map((page) => {
                  if (typeof page === "string") {
                    return (
                      <PaginationItem key={page} className="cursor-default">
                        <PaginationEllipsis className="cursor-default" />
                      </PaginationItem>
                    );
                  }

                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => handlePageChange(page)}
                        isActive={currentPage === page}
                        className={
                          currentPage === page
                            ? "bg-primary dark:bg-primary-900/50"
                            : ""
                        }
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-disabled={currentPage === totalPages}
                    className={
                      currentPage === totalPages
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </Card>
      {actsAsEmployee && (
        <div className="bg-white dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-500/10 rounded-full">
                <Wallet className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                  Leave Encashment
                </h2>
                <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">
                  Monetize your unused leave balance
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row divide-y lg:divide-y-0 lg:divide-x divide-slate-100 dark:divide-slate-800">
            {/* Left Section: Stats & Calculator */}
            <div className="lg:w-2/3 p-4 sm:p-6 space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="p-4 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-800">
                  <p className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">
                    Total Balance
                  </p>
                  <p className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white leading-none">
                    {encashMentData.total_available}{" "}
                    <span className="text-[10px] text-slate-400 uppercase font-bold">
                      Days
                    </span>
                  </p>
                </div>
                <div className="p-4 bg-emerald-50/50 dark:bg-emerald-500/5 rounded-2xl border border-emerald-100 dark:border-emerald-500/20">
                  <p className="text-[9px] sm:text-[10px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest mb-1">
                    Encashable
                  </p>
                  <p className="text-2xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400 leading-none">
                    {encashMentData.encashable}{" "}
                    <span className="text-[10px] opacity-60 uppercase font-bold">
                      Days
                    </span>
                  </p>
                </div>
              </div>

              {/* Slider Calculator */}
              <div className="bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-100 dark:border-slate-800 p-4 sm:p-6">
                <div className="flex items-center justify-between mb-6">
                  <h4 className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white">
                    Request Calculator
                  </h4>
                  <div className="text-[9px] sm:text-[10px] font-bold text-emerald-500 bg-emerald-500/10 px-2 py-1 rounded">
                    Est. ₹{encashMentData.fixedAmountPerDay}/ Day
                  </div>
                </div>

                <div className="space-y-6 dark:text-white">
                  <MultipleSelector
                    value={selectedLeaves}
                    onChange={(val) => {
                      console.log("MULTI SELECT CHANGE:", val);
                      setSelectedLeaves(Array.isArray(val) ? val : []);
                    }}
                    options={leaveOptions}
                    placeholder="Select Leaves"
                  />

                  {selectedLeaves.length > 0 && (
                    <div className="mt-2 space-y-3 p-4 rounded-2xl border border-slate-100 bg-slate-50/50 dark:bg-slate-900/50 dark:border-slate-800">
                      <div className="flex items-center gap-2 mb-3">
                        <Clock className="w-4 h-4 text-[#3194A0]" />
                        <h4 className="font-bold text-xs uppercase tracking-widest text-slate-500">
                          Leave Balances
                        </h4>
                      </div>

                      {selectedLeaves.map((item) => {
                        const leave = encashMentData.leaveBalances.find(
                          (l) => l.leave_id === item,
                        );

                        if (!leave) return null;

                        return (
                          <div
                            key={item.value}
                            className="flex items-center justify-between px-4 py-3 rounded-xl border border-slate-100 bg-white shadow-sm dark:bg-slate-800 dark:border-slate-700 transition-all hover:border-indigo-200 dark:hover:border-indigo-900"
                          >
                            <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                              {leave.leave_name}
                            </span>

                            <div className="flex items-center gap-3">
                              <div className="relative">
                                <Input
                                  type="number"
                                  min={0}
                                  max={leave.remaining}
                                  onWheel={(e) => e.currentTarget.blur()}
                                  className="w-20 text-center font-black text-indigo-600 dark:text-indigo-400 [&::-webkit-inner-spin-button]:appearance-none bg-slate-50/50 dark:bg-slate-900"
                                  value={leaveEncashDays[item] ?? ""}
                                  onChange={(e) => {
                                    const raw = e.target.value;

                                    // allow clearing input
                                    if (raw === "") {
                                      setLeaveEncashDays((prev) => ({
                                        ...prev,
                                        [item]: "",
                                      }));
                                      return;
                                    }

                                    const numeric = Number(raw);

                                    // clamp between 0 and remaining
                                    const clamped = Math.min(
                                      Math.max(numeric, 0),
                                      leave.remaining,
                                    );

                                    setLeaveEncashDays((prev) => ({
                                      ...prev,
                                      [item]: clamped,
                                    }));
                                  }}
                                />
                              </div>
                              <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">
                                / {leave.remaining}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 pt-2">
                    <div className="w-full sm:flex-1 p-3 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl shadow-sm text-center sm:text-left">
                      <p className="text-[9px] font-bold text-slate-400 uppercase mb-1">
                        Net Payoff (Estimate)
                      </p>

                      <p className="text-lg font-black text-slate-900 dark:text-white">
                        ₹{estimatedPayoff.toLocaleString("en-IN")}
                      </p>
                    </div>

                    <button
                      className="w-full sm:w-auto bg-[#3194A0] hover:bg-[#287d87] text-white font-bold py-3.5 px-8 rounded-xl transition-all flex items-center justify-center gap-2 text-[12px] sm:text-xs"
                      onClick={() => setConfirmOpen(true)}
                    >
                      Request
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Section: History */}
            <div className="lg:w-1/3 flex flex-col">
              <div className="px-6 py-4 bg-gray-50/50 dark:bg-gray-900/50 border-b lg:border-b-0 border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">
                  History
                </h4>
              </div>

              <div className="divide-y divide-slate-100 dark:divide-slate-800 overflow-y-auto max-h-[400px] lg:max-h-[400px]">
                {encashMentData.history && encashMentData.history.length > 0 ? (
                  encashMentData.history.map((item) => (
                    <div
                      key={item.id}
                      className="px-6 py-4 flex items-center justify-between hover:bg-slate-50/80 dark:hover:bg-slate-800/30 transition-colors group cursor-pointer"
                    >
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-10 h-10 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-full flex items-center justify-center shadow-sm shrink-0 group-hover:border-blue-300 dark:group-hover:border-blue-800 transition-colors">
                          <Banknote className="w-5 h-5 text-blue-500" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-slate-900 dark:text-white leading-tight mb-0.5">
                            Encashment Payout
                          </p>
                          <div className="flex items-center gap-2 text-[10px] text-slate-400 font-bold uppercase tracking-tight">
                            <span>
                              {new Date(item.requested_at).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              )}
                            </span>
                            <span className="w-1 h-1 rounded-full bg-slate-300" />
                            <span>{item.requested_days} Leave Days</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-1">
                        <p className="text-sm font-primary text-primary-900 dark:text-primary leading-none">
                          ₹ {item.estimated_amount}
                        </p>
                        <Badge variant="secondary">{item.status}</Badge>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-6 py-10 text-center">
                    <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
                      <Banknote className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      No encashment history yet
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Your encashment requests will appear here once submitted.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="max-w-md p-6 shadow-2xl">
          {/* Enhanced shadow/border */}
          <DialogHeader className="space-y-1">
            <div className="flex items-center space-x-3">
              <XCircle className="h-7 w-7 text-red-600 dark:text-red-400 flex-shrink-0" />
              <DialogTitle className="text-2xl font-bold text-red-700 dark:text-red-400">
                Confirm Cancellation
              </DialogTitle>
            </div>
            <CardDescription className="pt-1">
              You are about to cancel this leave request. Please confirm the
              details below.
            </CardDescription>
          </DialogHeader>
          <div className="space-y-5 py-4">
            {/* Summary of Leave being Cancelled (Improved Visuals) */}
            {cancellingLeave && (
              <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wider">
                  Request Details
                </h4>
                <div className="space-y-1">
                  <p className="text-base font-bold text-gray-900 dark:text-gray-100">
                    {cancellingLeave.leave_name}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium">Dates:</span>{" "}
                    {format(
                      new Date(cancellingLeave.start_date),
                      "dd MMM yyyy",
                    )}
                    {cancellingLeave.start_date !== cancellingLeave.end_date &&
                      ` - ${format(
                        new Date(cancellingLeave.end_date),
                        "dd MMM yyyy",
                      )}`}
                    &nbsp;({cancellingLeave.days} days)
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center space-x-2">
                    <span className="font-medium">Current Status:</span>
                    <Badge variant="secondary" className="capitalize">
                      {cancellingLeave.status}
                    </Badge>
                  </p>
                </div>
              </div>
            )}

            {/* Cancellation Reason Input */}
            <div>
              <Label
                htmlFor="cancellation_reason"
                className="text-sm font-medium"
              >
                Reason for Cancellation <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="cancellation_reason"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="E.g., Travel plans changed, feeling better now, shifted dates."
                rows={4}
                className="dark:text-gray-200 dark:placeholder:text-gray-500"
                required
              />
            </div>
          </div>
          {/* Action Buttons (DialogFooter) */}
          <DialogFooter className="flex-col sm:flex-row sm:justify-between pt-4">
            {" "}
            {/* Changed to justify-between for better spacing */}
            <Button
              onClick={() => setShowCancelDialog(false)}
              variant="outline"
              disabled={cancelling}
              className="w-full sm:w-auto order-2 sm:order-1 mt-2 sm:mt-0"
            >
              Go Back
            </Button>
            <Button
              onClick={handleCancelLeave}
              disabled={cancelling || !cancellationReason.trim()}
              className="w-full sm:w-auto flex items-center space-x-2 bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 order-1 sm:order-2"
            >
              {cancelling ? (
                <>
                  <Clock className="h-4 w-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <X className="h-4 w-4" />
                  <span>Cancel Leave</span>
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="flex flex-col sm:max-w-[500px] p-0 overflow-hidden rounded-2xl">
          <DialogHeader className="p-6 bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
            <div className="flex justify-between items-center w-full pr-6">
              <div>
                <DialogTitle className="text-xl font-bold flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-500" />
                  Leave Details
                </DialogTitle>
                <DialogDescription className="mt-1">
                  Applied on{" "}
                  {selectedRequest?.applied_date
                    ? format(
                        new Date(selectedRequest.applied_date),
                        "dd MMM yyyy",
                      )
                    : "N/A"}
                </DialogDescription>
              </div>
              <div className="flex flex-col items-end">
                {getStatusBadge(selectedRequest?.status)}
              </div>
            </div>
          </DialogHeader>

          <div className="min-h-0 flex-1 p-6 space-y-6 overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700">
                <p className="text-[10px] font-bold uppercase text-gray-400 mb-1">
                  Leave Type
                </p>
                <p className="font-semibold">{selectedRequest?.leave_name}</p>
              </div>
              <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700">
                <p className="text-[10px] font-bold uppercase text-gray-400 mb-1">
                  Duration
                </p>
                <p className="font-semibold">
                  {selectedRequest?.days} Days{" "}
                  {selectedRequest?.half_day && "(Half Day)"}
                </p>
                {selectedRequest?.half_day &&
                  selectedRequest?.half_day_start_time &&
                  selectedRequest?.half_day_end_time && (
                    <p className="mt-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                      Working hours: {selectedRequest.half_day_start_time} –{" "}
                      {selectedRequest.half_day_end_time}
                    </p>
                  )}
              </div>
            </div>

            <div className="flex items-center justify-between p-4 rounded-xl border border-blue-100 bg-blue-50/30 dark:border-blue-900/20 dark:bg-blue-900/10">
              <div className="text-center flex-1">
                <p className="text-[10px] font-bold uppercase text-blue-500 mb-1">
                  Start Date
                </p>
                <p className="font-bold">
                  {selectedRequest?.start_date &&
                    format(new Date(selectedRequest.start_date), "dd MMM yyyy")}
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-blue-300" />
              <div className="text-center flex-1">
                <p className="text-[10px] font-bold uppercase text-blue-500 mb-1">
                  End Date
                </p>
                <p className="font-bold">
                  {selectedRequest?.end_date &&
                    format(new Date(selectedRequest.end_date), "dd MMM yyyy")}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-gray-500">
                <MessageSquare className="h-4 w-4" />
                <span className="text-xs font-bold uppercase tracking-wider">
                  Reason for Leave
                </span>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 p-3 rounded-lg border border-gray-100 dark:border-gray-800 italic">
                "{selectedRequest?.reason || "No reason provided."}"
              </p>
            </div>

            {selectedRequest?.status === "pending" && selectedRequest?.approval_stage && (
              <p className="text-sm text-gray-700 dark:text-gray-300 bg-amber-50/50 dark:bg-amber-900/10 p-3 rounded-lg border border-amber-100 dark:border-amber-900/20">
                {selectedRequest.approval_stage === "manager"
                  ? `Waiting for ${selectedRequest.awaiting_manager?.name || "your manager"} to approve. An admin gives the final approval after that.`
                  : selectedRequest.manager_approval?.approved_by
                    ? `Approved by ${selectedRequest.manager_approval.approved_by}. Waiting for an admin's final approval.`
                    : "Waiting for an admin's approval."}
              </p>
            )}

            {selectedRequest?.admin_comment && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-amber-500">
                  <User className="h-4 w-4" />
                  <span className="text-xs font-bold uppercase tracking-wider">
                    Admin Comment
                  </span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 bg-amber-50/50 dark:bg-amber-900/10 p-3 rounded-lg border border-amber-100 dark:border-amber-900/20">
                  {selectedRequest.admin_comment}
                </p>
              </div>
            )}

            <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-500">
                  <Paperclip className="h-4 w-4" />
                  <span className="text-xs font-bold uppercase tracking-wider">
                    Attachments
                  </span>
                </div>
                {selectedRequest?.document_key ? (
                  <button
                    onClick={() =>
                      downloadLeaveDocument(selectedRequest.document_key)
                    }
                    className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1"
                  >
                    Download Document
                  </button>
                ) : (
                  <span className="text-xs text-gray-400">No attachments</span>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={"Leave Encashment"}
        message={
          "Submit leave encashment request for approval? This action cannot be undone."
        }
        onConfirm={async () => {
          try {
            applyLeaveEncashment();
          } catch (err) {
            toast.error("Failed to delete");
          }
        }}
      />
    </div>
  );
};

function EmployeeLeaveSkeleton({ employeeId }) {
  return (
    <div className="space-y-6">
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center border-b pb-4 dark:border-gray-700 gap-3">
        <div className="space-y-2">
          {!employeeId?.employeeId ? (
            <>
              <Skeleton className="h-8 w-52" />
              <Skeleton className="h-4 w-80" />
            </>
          ) : (
            <Skeleton className="h-8 w-40" />
          )}
        </div>
        <div className="flex gap-3">
          <Skeleton className="h-10 w-28" />
          <Skeleton className="h-10 w-40" />
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className="rounded-2xl">
            <CardContent className="p-5 space-y-3">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-3 w-28" />
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="rounded-2xl">
        <CardHeader>
          <Skeleton className="h-6 w-44" />
          <Skeleton className="h-4 w-60" />
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

export default EmployeeLeave;
